import React, { useState, useEffect } from 'react';
import { Menu, Search, Bell, PhoneCall } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { NotificationDropdown } from './NotificationDropdown'; 
import { pushNotificationsApi } from '../../api/notificationApis/push-notifications.api'; 

interface ExpertNavbarProps {
  onMenuClick: () => void;
  availability: {
    isAvailableChat: boolean;
    isAvailableCall: boolean;
    isAvailableVideoCall: boolean;
  };
  onToggleStatus: (mode: 'chat' | 'call' | 'video') => void;
  isUpdating: boolean;
}

export const ExpertNavbar: React.FC<ExpertNavbarProps> = ({ 
  onMenuClick, 
  availability, 
  onToggleStatus, 
  isUpdating 
}) => {
  const navigate = useNavigate(); 
  const activeSession = useSelector((state: any) => state.activeSession?.session);

  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0); 

  const fetchUnreadCount = async () => {
    try {
      const history = await pushNotificationsApi.getNotificationHistory();
      const unread = history.filter((n: any) => !n.is_read).length;
      setUnreadCount(unread);
    } catch (error) {
      console.error("Failed to fetch unread count", error);
    }
  };

  useEffect(() => {
    if (!isNotifOpen) {
      fetchUnreadCount();
    }
  }, [isNotifOpen]);

  return (
    <header className="bg-white border-b border-gray-200 min-h-[4rem] py-3 lg:py-0 flex flex-col lg:flex-row items-center justify-between px-6 sticky top-0 z-40 gap-4 lg:gap-0">
      
      <div className="flex items-center justify-between w-full lg:w-auto gap-4">
        <button onClick={onMenuClick} className="md:hidden text-gray-500 hover:text-gray-700">
          <Menu className="w-6 h-6" />
        </button>
        <div className="hidden md:flex items-center text-gray-400 bg-gray-100 px-3 py-2 rounded-lg w-64">
          <Search className="w-4 h-4 mr-2" />
          <input type="text" placeholder="Search appointments..." className="bg-transparent border-none outline-none text-sm w-full text-gray-600" />
        </div>
      </div>

      <div className="flex items-center gap-4 flex-wrap justify-end w-full lg:w-auto">
        
        {activeSession && (
          <button 
            onClick={() => navigate(`/${activeSession.type}/${activeSession.expertCode}`, { state: activeSession })}
            className="flex relative items-center gap-2 px-4 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full transition-all duration-200 cursor-pointer shadow-md"
            title="Return to Active Session"
          >
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white"></span>
            </span>
            <PhoneCall className="w-3.5 h-3.5" />
            <span className="text-xs font-bold whitespace-nowrap capitalize">
              Resume {activeSession.type}
            </span>
          </button>
        )}

        <div className="flex items-center gap-5 border-r border-gray-200 pr-5">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-gray-600">Accept chat</span>
            <button 
              disabled={isUpdating}
              onClick={() => onToggleStatus('chat')}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${availability.isAvailableChat ? 'bg-green-500' : 'bg-gray-300'} ${isUpdating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${availability.isAvailableChat ? 'translate-x-5' : 'translate-x-1'}`} />
            </button>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-gray-600">Accept call</span>
            <button 
              disabled={isUpdating}
              onClick={() => onToggleStatus('call')}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${availability.isAvailableCall ? 'bg-blue-500' : 'bg-gray-300'} ${isUpdating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${availability.isAvailableCall ? 'translate-x-5' : 'translate-x-1'}`} />
            </button>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-gray-600">Accept video call</span>
            <button 
              disabled={isUpdating}
              onClick={() => onToggleStatus('video')}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${availability.isAvailableVideoCall ? 'bg-purple-500' : 'bg-gray-300'} ${isUpdating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${availability.isAvailableVideoCall ? 'translate-x-5' : 'translate-x-1'}`} />
            </button>
          </div>
        </div>

        <div className="relative">
          <button 
            onClick={() => setIsNotifOpen(!isNotifOpen)}
            className="relative p-2 text-gray-400 hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
          >
            <Bell className="w-5 h-5" />
            
            {/* 🟢 Badge number shows here */}
            {unreadCount > 0 && (
              <span className="absolute top-0 right-0 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full border-2 border-white transform translate-x-1/4 -translate-y-1/4">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
            
          </button>
          
          <NotificationDropdown 
            isOpen={isNotifOpen} 
            onClose={() => setIsNotifOpen(false)} 
            onAllRead={() => setUnreadCount(0)} // 🟢 Instantly clear the red dot
          />
        </div>

      </div>
    </header>
  );
};